import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Alert,
  CircularProgress,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  ArrowBack,
  ShoppingCart,
  AccountBalance,
  Warning,
  CheckCircle,
  Payment
} from '@mui/icons-material';
import api from '../services/api';
import { brand } from '../theme/colors';

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const projectId = searchParams.get('projectId');
  const quoteRequestId = searchParams.get('quoteRequestId');

  const [cartItems, setCartItems] = useState([]);
  const [escrowBalance, setEscrowBalance] = useState(0);
  const [orderTotal, setOrderTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fundingAmount, setFundingAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('ecocash');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [quoteRequest, setQuoteRequest] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState('delivery'); // 'delivery' or 'pickup'
  const [pickupPersonName, setPickupPersonName] = useState('');
  const [pickupCode, setPickupCode] = useState(null);

  useEffect(() => {
    if (projectId) {
      loadCart();
      loadEscrowBalance();
      if (quoteRequestId) {
        loadQuoteRequest();
      }
    }
  }, [projectId, quoteRequestId]);

  const loadQuoteRequest = async () => {
    try {
      const response = await api.get(`/merchant-quotes/requests/${quoteRequestId}`);

      if (response.data.success) {
        const request = response.data.data;
        setQuoteRequest(request);
        
        // Extract delivery address from quote request
        if (request.delivery_address) {
          let address = request.delivery_address;
          if (typeof address === 'string') {
            try {
              address = JSON.parse(address);
            } catch (e) {
              address = { address: address };
            }
          }
          setDeliveryAddress(address);
        }
      }
    } catch (err) {
      console.error('Error loading quote request:', err);
    }
  };

  const loadCart = async () => {
    try {
      const response = await api.get(`/material-orders/cart?projectId=${projectId}`);

      if (response.data.success) {
        const items = response.data.data.cartItems || [];
        console.log('Cart items loaded:', items);
        setCartItems(items);

        // Calculate total including all fees per merchant/quote
        // Group items by quote_id to calculate fees once per quote
        const quotesMap = {};
        items.forEach(item => {
          const quoteId = item.quote_id;
          if (!quotesMap[quoteId]) {
            quotesMap[quoteId] = {
              items: [],
              subtotal: 0,
              delivery_fee: parseFloat(item.delivery_fee || 0),
              offloading_fee: parseFloat(item.offloading_fee || 0),
              other_charges: parseFloat(item.other_charges || 0),
              vat_amount: parseFloat(item.vat_amount || 0)
            };
          }
          quotesMap[quoteId].items.push(item);
          quotesMap[quoteId].subtotal += parseFloat(item.total_price || 0);
        });
        
        console.log('Quotes map with fees:', quotesMap);

        // Calculate grand total: sum of (subtotal + fees) for each quote
        const grandTotal = Object.values(quotesMap).reduce((sum, quote) => {
          const quoteTotal = quote.subtotal + quote.delivery_fee + quote.offloading_fee + 
                           quote.other_charges + quote.vat_amount;
          return sum + quoteTotal;
        }, 0);

        setOrderTotal(grandTotal);

        // Calculate shortfall
        const shortfall = grandTotal - escrowBalance;
        if (shortfall > 0) {
          setFundingAmount(Math.max(shortfall, 1000)); // Suggest at least $1000 or exact amount
        }
      }
    } catch (err) {
      console.error('Error loading cart:', err);
      setError('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const loadEscrowBalance = async () => {
    try {
      const response = await api.get(`/escrow/project/${projectId}`);

      if (response.data.success) {
        const escrow = response.data.data;
        // Use available_balance if provided, otherwise calculate it
        const availableBalance = escrow.available_balance !== undefined 
          ? parseFloat(escrow.available_balance || 0)
          : parseFloat(escrow.total_amount || 0) - parseFloat(escrow.held_amount || 0);
        console.log('Escrow balance loaded:', { escrow, availableBalance });
        setEscrowBalance(availableBalance);
      } else {
        // No escrow account yet, balance is 0
        console.log('No escrow account found for project:', projectId);
        setEscrowBalance(0);
      }
    } catch (err) {
      // No escrow account exists yet
      console.error('Error loading escrow balance:', err);
      setEscrowBalance(0);
    }
  };

  const handleFundEscrow = async () => {
    try {
      setProcessing(true);
      setError('');

      // Make sure the project wallet exists, then send the client to Stripe
      // Checkout to top it up by the shortfall amount.
      const ensureRes = await api.post('/material-orders/escrow/ensure', { projectId });
      const escrow = ensureRes.data?.data;
      if (!escrow?.id) {
        throw new Error('Could not prepare the project wallet');
      }

      const sessionRes = await api.post('/stripe/create-checkout-session', {
        escrowId: escrow.id,
        amount: fundingAmount,
        currency: 'USD',
        paymentMethodTypes: ['card'],
        successUrl: `${window.location.origin}/checkout?projectId=${projectId}&wallet_funded=true&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/checkout?projectId=${projectId}&wallet_cancelled=true`
      });

      const checkoutUrl = sessionRes.data?.data?.checkoutUrl || sessionRes.data?.checkoutUrl;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        throw new Error('Failed to get checkout URL');
      }
    } catch (err) {
      console.error('Error funding escrow:', err);
      setError(err.response?.data?.error || 'Failed to process payment. Please try again.');
      setProcessing(false);
    }
  };

  const handlePlaceOrder = async () => {
    try {
      setProcessing(true);
      setError('');

      // Group items by merchant
      const ordersByMerchant = {};
      cartItems.forEach(item => {
        const merchantId = item.merchant_id;
        if (!ordersByMerchant[merchantId]) {
          ordersByMerchant[merchantId] = [];
        }
        ordersByMerchant[merchantId].push(item);
      });

      // Validate pickup person name if pickup is selected
      if (deliveryMethod === 'pickup' && !pickupPersonName.trim()) {
        setError('Please provide the name of the person who will pick up the order');
        setProcessing(false);
        return;
      }

      // Get delivery address from quote request or use default (only for delivery)
      const finalDeliveryAddress = deliveryMethod === 'delivery' 
        ? (deliveryAddress || {
            street: '',
            suburb: '',
            city: '',
            province: '',
            postal_code: ''
          })
        : null;

      // Create orders for each merchant
      const createdOrders = [];
      for (const [merchantId, items] of Object.entries(ordersByMerchant)) {
        const orderData = {
          projectId,
          cartItems: items,
          deliveryMethod,
          estimatedDeliveryDate: null
        };

        if (deliveryMethod === 'delivery') {
          orderData.deliveryAddress = finalDeliveryAddress;
          orderData.deliveryInstructions = deliveryInstructions;
        } else if (deliveryMethod === 'pickup') {
          orderData.pickupPersonName = pickupPersonName;
        }

        const orderResponse = await api.post('/material-orders', orderData);

          if (orderResponse.data.success) {
            const orderData = Array.isArray(orderResponse.data.data) ? orderResponse.data.data : [orderResponse.data.data];
            createdOrders.push(...orderData);
          }
        }

        if (createdOrders.length > 0) {
          // Check if any order has a pickup code to display
          const pickupOrder = createdOrders.find(o => o.pickup_code);
          if (pickupOrder) {
            setSuccess(true);
            // Store pickup code in state to display
            setPickupCode(pickupOrder.pickup_code);
            setTimeout(() => {
              navigate(`/projects/${projectId}`);
            }, 5000); // Give more time to see the pickup code
          } else {
            setSuccess(true);
            setTimeout(() => {
              navigate(`/projects/${projectId}`);
            }, 2000);
          }
        }
    } catch (err) {
      console.error('Error placing order:', err);
      if (err.response?.data?.errorCode === 'QUOTE_EXPIRED' || 
          err.response?.data?.error?.includes('expired')) {
        setError('One or more quotes have expired. Please remove expired items from your cart and request new quotes. You will be redirected to the quote comparison page.');
        setTimeout(() => {
          if (quoteRequestId) {
            navigate(`/quotes/${quoteRequestId}`);
          } else {
            navigate(`/projects/${projectId}`);
          }
        }, 5000);
      } else if (err.response?.data?.error === 'Insufficient escrow balance') {
        setError('Insufficient wallet balance. Please fund your account first.');
      } else {
        setError(err.response?.data?.error || 'Failed to place order. Please try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const shortfall = orderTotal - escrowBalance;
  const hasSufficientFunds = escrowBalance >= orderTotal;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Checkout - Material Order
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {pickupCode ? (
            <Box>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Order placed successfully!
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Pickup Code:</strong>
              </Typography>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'primary.light',
                  borderRadius: 1,
                  textAlign: 'center',
                  mb: 2
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 2, color: 'primary.dark' }}>
                  {pickupCode}
                </Typography>
              </Box>
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Important:</strong> Share this pickup code with the person who will pick up the order. 
                  They will need to provide this code or their ID at the store.
                </Typography>
              </Alert>
              <Typography variant="body2" sx={{ mt: 2 }}>
                Redirecting to project page in a few seconds...
              </Typography>
            </Box>
          ) : (
            'Order placed successfully! Redirecting to project page...'
          )}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Order Summary */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Order Summary
            </Typography>

            {Object.entries(
              cartItems.reduce((acc, item) => {
                // Group by quote_id to show fees per quote
                const quoteId = item.quote_id;
                if (!acc[quoteId]) {
                  acc[quoteId] = {
                    merchant_name: item.merchant_name,
                    items: [],
                    subtotal: 0,
                    delivery_fee: parseFloat(item.delivery_fee || 0),
                    offloading_fee: parseFloat(item.offloading_fee || 0),
                    other_charges: parseFloat(item.other_charges || 0),
                    vat_percentage: parseFloat(item.vat_percentage || 0),
                    vat_amount: parseFloat(item.vat_amount || 0)
                  };
                }
                acc[quoteId].items.push(item);
                acc[quoteId].subtotal += parseFloat(item.total_price || 0);
                return acc;
              }, {})
            ).map(([quoteId, quote]) => {
              const quoteTotal = quote.subtotal + quote.delivery_fee + quote.offloading_fee + 
                                quote.other_charges + quote.vat_amount;
              return (
                <Box key={quoteId} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    FROM: {quote.merchant_name}
                  </Typography>
                  {quote.items.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">
                        • {item.material_name} ({item.quantity} {item.unit} @ ${parseFloat(item.unit_price).toFixed(2)})
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ${parseFloat(item.total_price).toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Subtotal:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ${quote.subtotal.toFixed(2)}
                      </Typography>
                    </Box>
                    {quote.delivery_fee > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">Delivery Fee:</Typography>
                        <Typography variant="body2">${quote.delivery_fee.toFixed(2)}</Typography>
                      </Box>
                    )}
                    {quote.offloading_fee > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">Offloading Fee:</Typography>
                        <Typography variant="body2">${quote.offloading_fee.toFixed(2)}</Typography>
                      </Box>
                    )}
                    {quote.other_charges > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">Other Charges:</Typography>
                        <Typography variant="body2">${quote.other_charges.toFixed(2)}</Typography>
                      </Box>
                    )}
                    {quote.vat_amount > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">
                          VAT {quote.vat_percentage > 0 ? `(${quote.vat_percentage.toFixed(1)}%)` : ''}:
                        </Typography>
                        <Typography variant="body2">${quote.vat_amount.toFixed(2)}</Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Quote Total:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ${quoteTotal.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                ORDER TOTAL:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                ${orderTotal.toFixed(2)}
              </Typography>
            </Box>
          </Paper>

          {/* Delivery Instructions */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Delivery Instructions (Optional)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="E.g., 'Deliver to construction site gate, contact foreman John on +263 77 XXX XXXX before delivery'"
              value={deliveryInstructions}
              onChange={(e) => setDeliveryInstructions(e.target.value)}
            />
          </Paper>
        </Grid>

        {/* Payment & Wallet */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountBalance />
              Payment from Wallet
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Your Wallet Balance:</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                ${escrowBalance.toFixed(2)}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Order Total:</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                ${orderTotal.toFixed(2)}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {hasSufficientFunds ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                <CheckCircle sx={{ mr: 1 }} />
                Sufficient funds available
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Warning sx={{ mr: 1 }} />
                Insufficient funds - ${Math.abs(shortfall).toFixed(2)} short
              </Alert>
            )}

            {!hasSufficientFunds && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  You need to add ${Math.abs(shortfall).toFixed(2)} to your wallet to complete this order.
                </Typography>

                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Amount to Add: *
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={fundingAmount}
                  onChange={(e) => setFundingAmount(parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                  }}
                  sx={{ mb: 2 }}
                />

                <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
                  <FormLabel component="legend">Payment Method for Top-Up:</FormLabel>
                  <RadioGroup
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <FormControlLabel value="ecocash" control={<Radio />} label="EcoCash (Mobile Money)" />
                    <FormControlLabel value="bank" control={<Radio />} label="Bank Transfer (RTGS/Swift)" />
                    <FormControlLabel value="card" control={<Radio />} label="Credit/Debit Card" />
                    <FormControlLabel value="cash" control={<Radio />} label="USD Cash Deposit" />
                  </RadioGroup>
                </FormControl>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleFundEscrow}
                  disabled={processing || fundingAmount <= 0}
                  startIcon={<Payment />}
                  sx={{
                    background: brand.gradient,
                    '&:hover': {
                      background: brand.gradientHover
                    }
                  }}
                >
                  Fund Wallet - ${fundingAmount.toFixed(2)}
                </Button>
              </Box>
            )}
          </Paper>

          {/* Terms & Conditions */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Terms & Conditions
            </Typography>
            <FormControlLabel
              control={<input type="checkbox" defaultChecked />}
              label="I agree to Eqaya's Terms of Service"
            />
            <FormControlLabel
              control={<input type="checkbox" defaultChecked />}
              label="I understand payment will be held in your wallet until delivery is confirmed"
            />
            <FormControlLabel
              control={<input type="checkbox" defaultChecked />}
              label="I will confirm delivery within 72 hours or payment will be automatically released"
            />
          </Paper>

          {/* Place Order Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handlePlaceOrder}
            disabled={processing || !hasSufficientFunds || success}
            startIcon={processing ? <CircularProgress size={20} /> : <ShoppingCart />}
            sx={{
              background: brand.gradient,
              '&:hover': {
                background: brand.gradientHover
              },
              '&:disabled': {
                background: 'grey.300'
              },
              py: 1.5
            }}
          >
            {processing ? 'Placing Order...' : hasSufficientFunds ? 'Place Order' : 'Fund Account First'}
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CheckoutPage;

